import { useEffect, useState } from 'react';
import { criteriaColor, criteriaName, criteriaNameInverse } from '../../utils';
import { ICorrectionComment } from '../../../../core/models/essay';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Button } from '../../../../core/components/atoms/Button';
import './style.css';
import { useEssayAuxiliarStore } from '../../stores/essayAuxiliarStore';
import { useBoardConfig } from '../../../../core/hooks/useBoardConfig';
import { useNavigate } from 'react-router-dom';
import { useEssayLessonInfo } from '../../hooks/useGetLessonInfos';

interface iCommentsSide {
  comments: string;
  correction: number;
  isPrint?: boolean;
  provider: string;
}

interface ICommentByCriteria {
  criteriaId: number;
  criteriaName?: string;
  criteriaColor: string;
  criteriaTitle: string;
  positives: number;
  negatives: number;
  comments: ICorrectionComment[];
}

interface ICommentByOutros {
  comment: string;
  criteria_id: number;
  exercise_list: string;
  id: number;
  positive: boolean;
  problem: number;
  problem_text: number;
  scale: number;
  shape: string;
  teacher_comment: string;
  type: string;
  type_id: number;
  video_id: string;
}

export function CommentsSide({
  comments,
  correction,
  isPrint = false,
  provider,
}: iCommentsSide) {
  const navigate = useNavigate();
  const { selected, setSelected } = useEssayAuxiliarStore();
  const { mutate, data, isPending } = useEssayLessonInfo();
  const { board } = useBoardConfig(correction);

  const [commentsArray, setCommentsArray] = useState<ICommentByCriteria[]>([]);
  const [loadingComment, setLoadingComment] = useState<number>();

  const groupByCriteria = (comments: ICorrectionComment[]) => {
    if (comments.length > 0) {
      const criterias = comments.map((it) => it.criteriaId);
      const criteriasClean = criterias.filter((numero, indice) => {
        return criterias.indexOf(numero) === indice;
      });

      const newCriterias = criteriasClean.map((crit) => {
        const comentarios = comments.filter((it) => it.criteriaId === crit);
        const positive = comentarios.filter(
          (comment) => comment.isPositive
        ).length;

        return {
          criteriaId: crit,
          criteriaColor: criteriaColor(crit),
          criteriaTitle: criteriaName(crit),
          positives: positive,
          negatives: comentarios.length - positive,
          comments: comentarios,
        };
      });

      return newCriterias;
    }

    return [];
  };

  const normalizeByAnother = (comments: ICommentByOutros[]) => {
    if (comments.length > 0) {
      const criterias = comments.map((it) => it.criteria_id);
      const criteriasClean = criterias.filter((numero, indice) => {
        return criterias.indexOf(numero) === indice;
      });

      const newCriterias = criteriasClean.map((crit) => {
        const comentarios = comments.filter((it) => it.criteria_id === crit);
        const positive = comentarios.filter(
          (comment) => comment.positive
        ).length;

        const formatedComments: ICorrectionComment[] = [];
        comentarios.map((it) => {
          const newObj = {
            correctionId: correction,
            criteriaId: it.criteria_id,
            end: null,
            height: null,
            id: it.id,
            isPositive: it.positive,
            left: null,
            problemId: it.problem,
            start: null,
            text: it.comment,
            top: null,
            width: null,
          };

          formatedComments.push(newObj);
        });

        return {
          criteriaId: crit,
          criteriaColor: criteriaColor(crit),
          criteriaTitle: criteriaName(crit),
          positives: positive,
          negatives: comentarios.length - positive,
          comments: formatedComments,
        };
      });

      return newCriterias;
    }

    return [];
  };

  const normalizeRO = (
    comments: { title: string; content: string; className: string }[]
  ) => {
    if (comments.length > 0) {
      const criterias = comments.map((it) => criteriaNameInverse(it.title));
      const criteriasClean = criterias.filter((numero, indice) => {
        return criterias.indexOf(numero) === indice;
      });

      const newCriterias = criteriasClean.map((crit) => {
        const comentarios = comments.filter(
          (it) => criteriaNameInverse(it.title) === crit
        );

        const formatedComments: ICorrectionComment[] = [];
        comentarios.map((it) => {
          const newObj = {
            correctionId: correction,
            criteriaId: criteriaNameInverse(it.title),
            end: null,
            height: null,
            id: criteriaNameInverse(it.title),
            isPositive: false,
            left: null,
            problemId: null,
            start: null,
            text: it.content,
            top: null,
            width: null,
          };

          formatedComments.push(newObj);
        });

        return {
          criteriaId: crit,
          criteriaColor: criteriaColor(crit),
          criteriaTitle: criteriaName(crit),
          positives: 0,
          negatives: 0,
          comments: formatedComments,
        };
      });

      return newCriterias;
    }

    return [];
  };

  useEffect(() => {
    if (comments && provider === 'ferrari') {
      const parsed = JSON.parse(comments);
      const criterias = groupByCriteria(parsed);

      setCommentsArray(criterias);
    } else if (provider === 'outros' && comments) {
      const parsed = JSON.parse(comments);
      const criterias = normalizeByAnother(parsed);

      setCommentsArray(criterias);
    } else {
      const parsed = JSON.parse(comments);
      const criterias = normalizeRO(parsed);

      setCommentsArray(criterias);
    }
  }, [comments]);

  const openLesson = (comment: ICorrectionComment) => {
    if (comment.materialLink) {
      if(comment.materialLink.indexOf('medio') >= 0) {
        setLoadingComment(comment.id);
        const splited = comment.materialLink?.split('/');
        
        mutate(splited[splited.length - 1]);
        return;
      }

      navigate(comment.materialLink);
    }
  };

  useEffect(() => {
    if (data && data.id) {
      const url = `/disciplinas/redacoes/modulo/${data.ancestors?.subarea?.slug}/assunto/${data.subjects[0].slug}/videoaula/${data.slug}`;
      navigate(url);
    }
  }, [data]);

  //TODO: componentizar
  return (
    <div
      className={`flex flex-col gap-md xs:pb-10 lg:pb-0`}
      style={{
        height: isPrint
          ? 'h-fit'
          : window.innerWidth < 992
            ? 'auto'
            : board.lines * 30 + 20,
      }}
    >
      <div className='flex flex-col gap-lg'>
        <Typography
          type='titleSection'
          size='h4'
          weight='bold'
          className={`uppercase ${isPrint && 'text-[#000]'}`}
        >
          Comentários do corretor
        </Typography>
        {provider !== 'redação_online' && (
          <div className='flex flex-row items-center gap-xxl'>
            <Typography size='small' className={`${isPrint && 'text-[#000]'}`}>
              <Icon
                Icon='thumb_up'
                variant='rounded'
                type='material'
                className={`text-sm ${isPrint && 'text-[#000]'}`}
              />{' '}
              = Pontos fortes
            </Typography>
            <Typography size='small' className={`${isPrint && 'text-[#000]'}`}>
              <Icon
                Icon='thumb_down'
                variant='rounded'
                type='material'
                className={`text-sm ${isPrint && 'text-[#000]'}`}
              />{' '}
              = Pontos de atenção
            </Typography>
          </div>
        )}
      </div>
      <div
        id='CommentsEssayContainer'
        className={`flex flex-col gap-sm ${isPrint ? 'h-fit' : 'h-full'} overflow-y-auto`}
      >
        {commentsArray.map((it, i) => (
          <div
            key={i}
            className='flex flex-col gap-lg rounded-md border-hairline p-4'
            style={{
              borderColor: it.criteriaColor,
              background: it.criteriaColor + '1A',
            }}
          >
            <div className='flex flex-row justify-between'>
              <Typography type='title' size='h5' weight='bold' className={`${isPrint && 'text-[#000]'}`}>
                {it.criteriaTitle}
              </Typography>
              {provider !== 'redação_online' && (
                <div className='flex flex-row items-center gap-lg'>
                  <Typography size='small' className={`${isPrint && 'text-[#000]'}`}>
                    <Icon
                      Icon='thumb_up'
                      variant='rounded'
                      type='material'
                      className='text-sm'
                    />{' '}
                    {it.positives}
                  </Typography>
                  <Typography size='small' className={`${isPrint && 'text-[#000]'}`}>
                    <Icon
                      Icon='thumb_down'
                      variant='rounded'
                      type='material'
                      className='text-sm'
                    />{' '}
                    {it.negatives}
                  </Typography>
                  <Typography size='small' className={`${isPrint && 'text-[#000]'}`}>
                    <strong>Total</strong> {it.comments.length}
                  </Typography>
                </div>
              )}
            </div>
            {it.comments.map((comment, i) => (
              <div
                className={`
                  flex cursor-pointer flex-col gap-sm pb-4 
                  ${it.comments.length > 1 ? 'border-b-hairline border-mono-600 last:border-none' : ''}
                  relative
                `}
                key={i}
                onClick={() =>
                  provider === 'ferrari'
                    ? setSelected(selected === comment.id ? null : comment.id)
                    : {}
                }
              >
                <div
                  className={`${selected === comment.id ? 'absolute' : 'hidden'} h-full w-full rounded-sm bg-mono-color-black opacity-20`}
                />
                <div className='flex flex-row items-start gap-sm'>
                  <Icon
                    Icon={comment.isPositive ? 'thumb_up' : 'thumb_down'}
                    type='material'
                    className={`mt-1 text-md ${isPrint ? 'text-[#000]' : 'text-mono-color-black'}`}
                  />
                  <Typography className={`text-left italic ${isPrint && 'text-[#000]'}`}>
                    {comment.text}
                  </Typography>
                </div>
                {comment.materialLink && (
                  <Button
                    key={i}
                    variant='text'
                    LeftIcon='play_circle'
                    iconType='material'
                    iconVariant='rounded'
                    className={`p-0 text-sm ${isPrint && 'text-[#000]'}`}
                    classNameIcon='text-md'
                    loading={isPending && loadingComment === comment.id}
                    onClick={() => openLesson(comment)}
                  >
                    Videoaula sobre o assunto
                  </Button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
